import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardShell } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import { ReportFilters, StatCard, useReportRange } from "@/components/ReportFilters";
import { buildRange, downloadCsv, fetchSuperAdminReport } from "@/lib/reports";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

function titleCase(s: string) {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="border-b border-border px-4 py-3 text-sm font-semibold">{title}</div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function BreakdownList({
  rows,
  empty,
}: {
  rows: { status: string; count: number }[];
  empty: string;
}) {
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">{empty}</p>;
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <ul className="space-y-2.5">
      {rows.map((r) => (
        <li key={r.status}>
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">{titleCase(r.status)}</span>
            <span className="tabular-nums text-muted-foreground">{r.count}</span>
          </div>
          <div className="mt-1 h-1.5 w-full rounded-full bg-muted">
            <div
              className="h-1.5 rounded-full bg-brand"
              style={{ width: `${(r.count / max) * 100}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function SuperAdminReportsPage() {
  const { preset, from, to, onChange } = useReportRange("30d");
  const range = useMemo(() => buildRange(preset, from, to), [preset, from, to]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["super-admin-report", range.from, range.to],
    queryFn: () => fetchSuperAdminReport(range),
  });

  const exportCsv = () => {
    if (!data) return;
    const rows: (string | number)[][] = [
      ["Landoo Platform Report"],
      ["Period", range.label],
      [],
      ["Active Services", "Active", "Pending", "Suspended"],
      ...data.activeServices.map((s) => [s.label, s.active, s.pending, s.suspended]),
      [],
      ["Subscription Summary", "Count"],
      ...data.subscriptions.map((s) => [titleCase(s.status), s.count]),
      ["Total", data.subscriptionTotal],
      [],
      ["Verification Summary", "Count"],
      ...data.verifications.map((v) => [titleCase(v.status), v.count]),
      ["Total", data.verificationTotal],
      [],
      ["Platform Growth (last 6 months)", "New Users", "New Partner Services", "New Listings"],
      ...data.growth.map((g) => [g.month, g.users, g.partners, g.listings]),
    ];
    downloadCsv(`landoo-platform-report-${range.from.slice(0, 10)}.csv`, rows);
  };

  const totalActive = data?.activeServices.reduce((a, b) => a + b.active, 0) ?? 0;

  return (
    <DashboardShell title="Reports" role="Super Admin" items={SUPER_ADMIN_NAV}>
      <div className="space-y-5">
        <ReportFilters
          preset={preset}
          from={from}
          to={to}
          onChange={onChange}
          onExportCsv={exportCsv}
        />
        <p className="text-sm text-muted-foreground">Showing data for {range.label}</p>

        {error && (
          <p className="text-sm text-coral">Could not load reports. {(error as Error).message}</p>
        )}
        {isLoading && <p className="text-sm text-muted-foreground">Loading report…</p>}

        {data && (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard label="Active Services" value={totalActive} />
              <StatCard label="Subscriptions" value={data.subscriptionTotal} />
              <StatCard label="Verification Requests" value={data.verificationTotal} />
              <StatCard
                label="New Listings (6 mo)"
                value={data.growth.reduce((a, b) => a + b.listings, 0)}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <Panel title="Active services">
                <ul className="space-y-3">
                  {data.activeServices.map((s) => (
                    <li key={s.service} className="flex items-center justify-between text-sm">
                      <span className="font-medium">{s.label}</span>
                      <span className="text-muted-foreground">
                        <span className="font-semibold text-foreground">{s.active}</span> active ·{" "}
                        {s.pending} pending · {s.suspended} suspended
                      </span>
                    </li>
                  ))}
                </ul>
              </Panel>
              <Panel title="Subscription summary">
                <BreakdownList rows={data.subscriptions} empty="No subscriptions in this period." />
              </Panel>
              <Panel title="Verification summary">
                <BreakdownList
                  rows={data.verifications}
                  empty="No verification requests in this period."
                />
              </Panel>
            </div>

            <Panel title="Platform growth — last 6 months">
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.growth} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
                    <YAxis allowDecimals={false} tickLine={false} axisLine={false} fontSize={12} />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="users"
                      name="New users"
                      fill="var(--sale)"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="partners"
                      name="Partner services"
                      fill="var(--rent)"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="listings"
                      name="Listings"
                      fill="var(--lease)"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </>
        )}
      </div>
    </DashboardShell>
  );
}

export const Route = createFileRoute("/_authenticated/super-admin/reports")({
  head: () => ({
    meta: [
      { title: "Reports · Landoo Super Admin" },
      {
        name: "description",
        content: "Platform-wide service, subscription, verification and growth reports for Landoo.",
      },
      { property: "og:title", content: "Reports · Landoo Super Admin" },
      {
        property: "og:description",
        content: "Platform-wide service, subscription, verification and growth reports for Landoo.",
      },
    ],
  }),
  component: SuperAdminReportsPage,
});
